import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';

import { cn } from '../../lib/cn';
import styles from './Toast.module.css';

export type ToastTone = 'success' | 'error' | 'info';

export type ToastAction = {
  readonly label: string;
  readonly onClick: () => void;
};

export type ToastInput = {
  readonly message: string;
  readonly tone?: ToastTone;
  readonly code?: string;
  readonly action?: ToastAction;
  readonly durationMs?: number;
};

export type ToastHandle = {
  success: (message: string, options?: Omit<ToastInput, 'message' | 'tone'>) => void;
  error: (message: string, options?: Omit<ToastInput, 'message' | 'tone'>) => void;
  info: (message: string, options?: Omit<ToastInput, 'message' | 'tone'>) => void;
  show: (input: ToastInput) => void;
};

type ToastRecord = ToastInput & { readonly id: number };

const ToastContext = createContext<ToastHandle | null>(null);

const DEFAULT_DURATION_MS = 4_000;

/** Стек справа снизу, 4 с (docs/06 §4). */
export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (input: ToastInput) => {
      const id = nextId.current;
      nextId.current += 1;
      setToasts((current) => [...current, { ...input, id }]);
      const duration = input.durationMs ?? DEFAULT_DURATION_MS;
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  const handle = useMemo<ToastHandle>(
    () => ({
      show,
      success: (message, options) => show({ ...options, message, tone: 'success' }),
      error: (message, options) => show({ ...options, message, tone: 'error', durationMs: options?.durationMs ?? 6_000 }),
      info: (message, options) => show({ ...options, message, tone: 'info' }),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={handle}>
      {children}
      <div className={styles['viewport']} aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => (
          <div key={toast.id} role="status" className={cn(styles['toast'], styles[toast.tone ?? 'info'])}>
            <span aria-hidden className={styles['icon']}>
              {toast.tone === 'success' ? (
                <CircleCheck size={16} />
              ) : toast.tone === 'error' ? (
                <CircleAlert size={16} />
              ) : (
                <Info size={16} />
              )}
            </span>
            <div className={styles['content']}>
              <p className={styles['message']}>{toast.message}</p>
              {toast.code ? <p className={styles['code']}>Код: {toast.code}</p> : null}
            </div>
            {toast.action ? (
              <button
                type="button"
                className={styles['action']}
                onClick={() => {
                  dismiss(toast.id);
                  toast.action?.onClick();
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
            <button
              type="button"
              aria-label="Закрыть уведомление"
              className={styles['close']}
              onClick={() => dismiss(toast.id)}
            >
              <X aria-hidden size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastHandle {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast нужно вызывать внутри ToastProvider');
  }
  return context;
}
